'use client';
import React, { useRef, useState } from 'react';
import { Tag } from '../ui/Tag';
import { HIGHLIGHT_DESCRIPTIONS, isTopRated, type TagVariant } from '@repo/utils';
import { cn } from '@/lib/cn';
import {
  IconFuture,
  IconGoodCause,
  IconRentStabilized,
  IconTopRated,
  IconVerified,
  IconWorst,
} from '@repo/svgs';
import type { Building } from '@repo/api';
import { useDeviceType } from '@/hooks/useDeviceType';
import { BottomSheet, InfoPopover } from '../ui';

const ICON_BY_VARIANT: Record<TagVariant, React.ComponentType<any>> = {
  verified: IconVerified,
  topRated: IconTopRated,
  rentStabilized: IconRentStabilized,
  goodCause: IconGoodCause,
  future: IconFuture,
  worstLandlord: IconWorst,
};

function TagHighlightsContent({ tags }: { tags: TagVariant[] }) {
  return (
    <div className={cn('flex flex-col gap-1')}>
      {tags.map((variant, index) => {
        const highlight = HIGHLIGHT_DESCRIPTIONS[variant];
        const IconComponent = ICON_BY_VARIANT[variant];

        if (!highlight || !IconComponent) return null;

        return (
          <div key={variant}>
            <div className={cn('flex gap-4 items-start px-2 py-3')}>
              <div className={cn('w-6 h-6 flex-shrink-0')}>
                <IconComponent className="w-full h-full" />
              </div>

              <div className={cn('flex-1 flex flex-col gap-1')}>
                <p className={cn('text-body-2-high text-primary')}>{highlight.title}</p>
                <p className={cn('text-caption text-secondary')}>{highlight.description}</p>
              </div>
            </div>

            {index < tags.length - 1 && <div className={cn('h-px bg-tertiary mx-2')} />}
          </div>
        );
      })}
    </div>
  );
}

function BuildingCardTags({
  building,
  displayTagsCount = 1,
}: {
  building: Building;
  displayTagsCount?: number;
}) {
  const { isMobile } = useDeviceType();
  const [showTagDetails, setShowTagDetails] = useState(false);
  const tagContainerRef = useRef<HTMLDivElement | null>(null);
  const isRentStabilized = building.openDataSummary?.isRentStabilized ?? false;
  const hasGoodCause = building.openDataSummary?.isGoodCauseEviction ?? false;
  const tags: TagVariant[] = [
    isTopRated(building.averageRating, building.reviewCount) && 'topRated',
    isRentStabilized && 'rentStabilized',
    hasGoodCause && 'goodCause',
  ].filter(Boolean) as TagVariant[];

  const displayTags = tags.slice(0, displayTagsCount);

  const handleTagClick = (e: React.MouseEvent) => {
    e.preventDefault(); // Prevent navigation to listing page
    e.stopPropagation(); // Prevent event bubbling
    setShowTagDetails(true);
  };

  const handleCloseTagDetails = () => {
    setShowTagDetails(false);
  };

  // Don't render anything if no tags
  if (displayTags.length === 0) {
    return null;
  }
  return (
    <>
      <div
        ref={tagContainerRef}
        className="absolute top-3 left-3 flex flex-wrap gap-2 max-w-[calc(100%-6rem)]"
      >
        {displayTags.map(tagVariant => (
          <Tag onClick={handleTagClick} key={tagVariant} variant={tagVariant} />
        ))}
      </div>
      {/* Tag Details Modal/Bottom Sheet */}
      {tags.length > 0 && (
        <>
          {isMobile ? (
            <BottomSheet
              open={showTagDetails}
              onOpenChange={setShowTagDetails}
              label="Building highlights"
              onClose={handleCloseTagDetails}
              bodyClassName="!px-4 !py-2"
            >
              <TagHighlightsContent tags={tags} />
            </BottomSheet>
          ) : (
            // Desktop InfoPopover
            <InfoPopover
              open={showTagDetails}
              onOpenChange={isOpen => !isOpen && handleCloseTagDetails()}
              onClose={handleCloseTagDetails}
              label="Building highlights"
              {...(tagContainerRef && { anchorRef: tagContainerRef })}
              placement="below"
              align="center"
              offsetY={8}
              bodyClassName="px-4 py-2"
            >
              <TagHighlightsContent tags={tags} />
            </InfoPopover>
          )}
        </>
      )}
    </>
  );
}

export default BuildingCardTags;
